import React, { useState, useMemo, useEffect } from 'react';
import { Input, Form, message, Select, Avatar as AntdAvatar } from 'antd';
import { Modal, Editable, FormItem, Avatar, ImageInput } from 'components/elements';
import { Header, GroupHeaderRow, ChipsRow, Chip, OptionRow } from './chatGroupModal.styled';
import { ResizeImageResult } from 'helpers';

type Contact = {
  id: string;
  username?: string;
  avatarUrl?: string;
  group?: number;
};

interface ChatGroupModalProps {
  modalVisible?: boolean;
  setModal?: (visible: boolean) => void;
  contacts?: Contact[];
  initialValues?: Partial<FromFields>;
  onCreateGroup: (payload: FromFields) => Promise<void> | void; // NEW
}

interface FromFields {
  id?: string;
  name: string;
  members: string[];
  avatar: ResizeImageResult;
  editing: boolean;
}

const ChatGroupModal = ({
  modalVisible,
  setModal,
  contacts = [],
  initialValues,
  onCreateGroup, // NEW
}: ChatGroupModalProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [groupForm] = Form.useForm();
  const selectedIds: string[] = Form.useWatch('members', groupForm) || [];

  const options = useMemo(
    () =>
      contacts.filter((contact) => (!contact.group)).map((u) => {
        const name = u?.username || u.id;
        const avatarUrl = u?.avatarUrl;

        const node = (
          <OptionRow>
            <AntdAvatar size={32} src={avatarUrl}>
              {name?.[0]}
            </AntdAvatar>
            <div className="meta">
              <span className="name">{name}</span>
            </div>
          </OptionRow>
        );

        return {
          value: u.id,
          label: node,     // rendered in dropdown
          name,            // used for searching
          rawLabel: name,  // used for tag chips via tagRender
          avatarUrl,
        } as any;
      }),
    [contacts]
  );

  const selectedUsers = useMemo(
    () => contacts.filter((c) => selectedIds?.includes(c.id) && c.group === 0),
    [contacts, selectedIds]
  );

  useEffect(() => {
    if (modalVisible) {
      if (initialValues && initialValues.editing) {
        // Prefill id + other fields
        groupForm.setFieldsValue({ id: initialValues.id, ...initialValues });
      } else {
        groupForm.resetFields();
      }
    }
  }, [modalVisible, initialValues, groupForm]);

  const titleText = initialValues?.editing ? 'Edit New Group' : 'Create New Group';

  return (
    <Modal
      title={titleText}
      open={modalVisible}
      onRightText={initialValues?.editing ? 'Save' : 'Create'}
      onCancel={() => setModal?.(false)}
      onRight={() => groupForm.submit()}
      onRightProps={{ loading: submitting }}
    >
      <Form
        layout="vertical"
        form={groupForm}
        onFinish={async (vals: FromFields) => {
          try {
            setSubmitting(true);
            // Ensure id is included (from form or fallback to initialValues)
            const payload = {
              ...vals,
              id: vals.id || initialValues?.id,
              editing: !!initialValues?.editing,
            };
            await onCreateGroup(payload); // delegate to ChatView
            setModal?.(false);
            groupForm.resetFields();
            message.success('Group ' + (initialValues?.editing ? 'updated' : 'created') + ' successfully!');
          } catch (err) {
            if (err instanceof Error) message.error(err.message);
          } finally {
            setSubmitting(false);
          }
        }}
        validateTrigger="onChange"
      >
        {/* hidden id field so vals.id exists */}
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>

        <GroupHeaderRow>
          <FormItem name="avatar" noStyle bordered>
            <Editable
              isEditable={true}
              preview={({ value }) => <Avatar avatarUrl={value?.url} size="lg" />}
              input={({ value, onChange }) => (
                <ImageInput width={50} height={50} cropping onChange={onChange}>
                  <div className="avatar-col">
                    <Avatar avatarUrl={value?.url} size="sm" editable />
                  </div>
                </ImageInput>
              )}
            />
          </FormItem>
          {/* 
          <ImageInput
            width={200}
            height={200}
            cropping
            onChange={(res) => groupForm.setFieldsValue({ avatar: res })}
          >
            <div className="avatar-col">
              <Avatar size="sm" editable avatarUrl={avatarVal?.url} />
            </div>
          </ImageInput> */}

          <div className="name-col">
            <FormItem
              theme="dark"
              label="Group Name"
              name="name"
              rules={[{ required: true, message: 'Please input group name!' }]}
            >
              <Input placeholder="My Awesome Group" />
            </FormItem>
          </div>
        </GroupHeaderRow>

        {/* Header with count + selected chips */}
        <Header>
          <div style={{ fontWeight: 600 }}>
            Members {selectedIds.length}
          </div>
        </Header>

        {!!selectedUsers.length && (
          <ChipsRow>
            {selectedUsers.map((u) => {
              const name = u?.username || u.id;
              const avatarUrl = u?.avatarUrl;
              return (
                <Chip key={u.id} closable onClose={() => {
                  const next = (selectedIds || []).filter((id) => id !== u.id);
                  groupForm.setFieldsValue({ members: next });
                }}>
                  <AntdAvatar size={20} src={avatarUrl}>
                    {name?.[0]}
                  </AntdAvatar>
                  {name}
                </Chip>
              );
            })}
          </ChipsRow>
        )}

        <FormItem
          theme="dark"
          label="Members"
          name="members"
          rules={[{ required: true, message: 'Please select at least one member!' }]}
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Search users"
            style={{ width: '100%' }}
            options={options}
            // Search by our custom "name" field
            optionFilterProp="name"
            filterOption={(input, option) =>
              ((option as any)?.name || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            // Compact chip with avatar + name
            tagRender={(tagProps) => {
              const { value, closable, onClose } = tagProps;
              const opt = (options as any[]).find((o) => o.value === value);
              return (
                <Chip closable={closable} onClose={onClose}>
                  <AntdAvatar size={18} src={opt?.avatarUrl}>
                    {(opt?.rawLabel || '')[0]}
                  </AntdAvatar>
                  {opt?.rawLabel}
                </Chip>
              );
            }}
            // Dark-ish dropdown styling (optional)
            dropdownStyle={{ maxHeight: 360, overflow: 'auto' }}
          />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default ChatGroupModal;